import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, REQUEST_TIMEOUT } from '../config/api.config';
import { msalInstance } from '../auth/AuthProvider';
import { apiRequest } from '../config/auth.config';

/**
 * Shared types used across API services.
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface Article {
  id: number;
  title: string;
  description: string;
  url: string;
  source: string;
  publishedAt: string;
  collectedAt: string;
  category: string;
  commodities: string[];
  countries: string[];
  companies: string[];
  significance: number;
  thumbnailUrl?: string;
  isSignificant: boolean;
  language?: string;
}

export interface ArticleFilters {
  category?: string;
  commodity?: string;
  country?: string;
  company?: string;
  source?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  language?: string;
}

export interface DashboardSummary {
  articlesToday: number;
  activeSources: number;
  countriesMonitored: number;
  significantItems: number;
  articlesByCategory: { category: string; count: number }[];
  articlesOverTime: { date: string; count: number }[];
  topSources: { source: string; count: number }[];
  topHeadlines: Article[];
  commodityMentions: { commodity: string; count: number }[];
  recentByCommodity: Record<string, Article[]>;
  trends: {
    articlesToday: { direction: 'up' | 'down' | 'neutral'; value: string };
    activeSources: { direction: 'up' | 'down' | 'neutral'; value: string };
    countriesMonitored: { direction: 'up' | 'down' | 'neutral'; value: string };
    significantItems: { direction: 'up' | 'down' | 'neutral'; value: string };
  };
}

export interface CameroonSummary {
  totalArticles: number;
  articlesThisWeek: number;
  byLanguage: Record<string, number>;
  topCommodities: { commodity: string; count: number }[];
}


export interface CameroonNewsletterSection {
  thisWeeksSignal: { headline: string; body: string; };
  byTheNumbers: { figures: { value: string; label: string; context: string; }[] };
  strategicInsight: { heading: string; analysis: string; implications: string; };
  barazaCorner: { text: string; };
  worthReading: { title: string; source: string; url: string; teaser: string; }[];
}

export interface CameroonAIContentLang {
  blogArticle: {
    title: string;
    body: string;
    metaDescription: string;
  };
  linkedinPost: {
    text: string;
    hashtags: string[];
  };
  strategicInsights: {
    title: string;
    insights: {
      heading: string;
      analysis: string;
      implications: string;
    }[];
  };
  newsletter: CameroonNewsletterSection;
}

export interface CameroonAIContent {
  en: CameroonAIContentLang;
  fr: CameroonAIContentLang;
}

export interface CameroonContentPreview {
  id: number;
  title: string;
  status: string;
  reportLanguage: string;
  htmlPreview: string;
  aiContent: CameroonAIContent | null;
  pairedReportId: number | null;
  articleCount: number;
  dateFrom: string;
  dateTo: string;
}
export interface Report {
  id: number;
  title: string;
  dateFrom: string;
  dateTo: string;
  articleCount: number;
  fileSize: string;
  status: 'ready' | 'generating' | 'failed' | 'draft' | 'completed' | 'approved';
  reportType?: string;
  reportLanguage?: string;
  pairedReportId?: number;
  createdAt: string;
  downloadUrl?: string;
}

export interface BarazaPreview {
  id: number;
  title: string;
  status: string;
  reportLanguage: string;
  htmlPreview: string;
  pairedReportId: number | null;
  articleCount: number;
  dateFrom: string;
  dateTo: string;
}



export interface BarazaThematicPost {
  title: string;
  text: string;
  hashtags: string[];
}

export interface BarazaAIContentLang {
  weeklyRoundup: {
    title: string;
    summary: string;
    highlights: string[];
  };
  linkedinPost: {
    text: string;
    hashtags: string[];
  };
  carousel: {
    topic: string;
    slides: {
      slideNumber: number;
      title: string;
      content: string;
      keyFigure?: string;
      analysis: string;
    }[];
  };
  thematicPosts: {
    governance: BarazaThematicPost;
    industrialization: BarazaThematicPost;
    digitalIntelligence: BarazaThematicPost;
  };
}

export interface BarazaAIContent {
  en: BarazaAIContentLang;
  fr: BarazaAIContentLang;
}

export interface BarazaAIContentPreview {
  id: number;
  title: string;
  status: string;
  reportLanguage: string;
  htmlPreview: string;
  aiContent: BarazaAIContent | null;
  pairedReportId: number | null;  // Legacy — null for new bilingual reports
  articleCount: number;
  dateFrom: string;
  dateTo: string;
}

export interface BarazaNewsArticle {
  id: number;
  title: string;
  url: string;
  source: string | null;
  publishedAt: string;
  language: string;
  country: string | null;
  description: string | null;
}

export interface User {
  id: number;
  displayName: string;
  email: string;
  role: 'admin' | 'internal' | 'external';
  isActive: boolean;
  accessExpiresAt?: string | null;
  lastLoginAt?: string | null;
  createdAt: string;
  invitedById?: number | null;
  permittedTabs?: string[] | null;
}

export interface Invitation {
  id: number;
  email: string;
  displayName?: string;
  accessDurationDays: number;
  durationDays?: number; // alias for backward compat
  expiresAt: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  createdAt: string;
  createdBy?: string;
  inviterName?: string;
  acceptedAt?: string | null;
}

export interface ParsedSources {
  companies: { name: string }[];
  countries: { country: string; product: string; mine: string }[];
  websites: { url: string }[];
  eitiSources: { country: string; website: string }[];
  asmSources: { source: string; type: string; country: string; website: string }[];
  linkedinAccounts: { name: string; url: string }[];
  summary: {
    companiesCount: number;
    countriesCount: number;
    websitesCount: number;
    eitiSourcesCount: number;
    asmSourcesCount: number;
    linkedinAccountsCount: number;
    totalEntries: number;
  };
}

/**
 * Axios instance configured with auth interceptors and error handling.
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor: attach bearer token from MSAL.
 * For CIAM (Entra External ID), we MUST use the id_token as the bearer token
 * because CIAM does not expose custom API scopes. The accessToken returned by
 * acquireTokenSilent targets Microsoft Graph (aud: "https://graph.microsoft.com"),
 * so our backend would reject it with an audience mismatch. The idToken has
 * aud = our client ID, which the backend validates correctly.
 */
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const account = msalInstance.getActiveAccount();
    if (account) {
      try {
        const response = await msalInstance.acquireTokenSilent({
          ...apiRequest,
          account,
        });
        // Prefer idToken — the accessToken is for MS Graph, not our API
        const token = response.idToken || response.accessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('[API] Silent token acquisition failed, proceeding without token');
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor: handle 401 and 403 errors globally.
 * Uses a guard to prevent infinite logout loops (401 → logout → login → 401 …).
 */
let isLoggingOut = false;

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401 && !isLoggingOut) {
      // Token expired or invalid — redirect to login (once only)
      isLoggingOut = true;
      console.warn('[API] 401 Unauthorized — redirecting to login');
      try {
        await msalInstance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin + '/login',
        });
      } catch {
        window.location.href = '/login';
      }
    }

    if (error.response?.status === 403) {
      console.warn('[API] 403 Forbidden — access expired or insufficient permissions');
    }

    return Promise.reject(error);
  }
);

export default api;
